import {
  IWeeklyScheduleDay,
  IAvailabilityException,
  ICreateAvailabilityExceptionRequest,
  IUpdateAvailabilityExceptionRequest,
  IDayAvailability,
} from '../interfaces/heavyDutyMachinery/IAvailability';
import { apiService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

export const getSchedule = async (spaceId: string): Promise<IWeeklyScheduleDay[]> => {
  const response = await apiService<IWeeklyScheduleDay[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SCHEDULE(spaceId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load schedule');
};

export const updateSchedule = async (spaceId: string, days: IWeeklyScheduleDay[]): Promise<IWeeklyScheduleDay[]> => {
  const response = await apiService<IWeeklyScheduleDay[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SCHEDULE(spaceId),
    method: HTTP_METHODS.PUT,
    body: { days },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to update schedule');
};

export const getExceptions = async (spaceId: string, from?: string, to?: string): Promise<IAvailabilityException[]> => {
  const params: Record<string, string> = {};
  if (from) params.from = from;
  if (to) params.to = to;
  const response = await apiService<IAvailabilityException[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_EXCEPTIONS(spaceId),
    method: HTTP_METHODS.GET,
    params,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load exceptions');
};

export const createException = async (
  spaceId: string,
  request: ICreateAvailabilityExceptionRequest
): Promise<IAvailabilityException> => {
  const response = await apiService<IAvailabilityException>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_EXCEPTIONS(spaceId),
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to create exception');
};

export const updateException = async (
  spaceId: string,
  exceptionId: string,
  request: IUpdateAvailabilityExceptionRequest
): Promise<IAvailabilityException> => {
  const response = await apiService<IAvailabilityException>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_EXCEPTION_BY_ID(spaceId, exceptionId),
    method: HTTP_METHODS.PUT,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to update exception');
};

export const deleteException = async (spaceId: string, exceptionId: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_EXCEPTION_BY_ID(spaceId, exceptionId),
    method: HTTP_METHODS.DELETE,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to delete exception');
};

export const getAvailability = async (spaceId: string, date: string): Promise<IDayAvailability> => {
  const response = await apiService<IDayAvailability>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_AVAILABILITY(spaceId),
    method: HTTP_METHODS.GET,
    params: { date },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load availability');
};

export const getAvailabilityRange = async (spaceId: string, from: string, to: string): Promise<IDayAvailability[]> => {
  const response = await apiService<IDayAvailability[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_AVAILABILITY_RANGE(spaceId),
    method: HTTP_METHODS.GET,
    params: { from, to },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load availability range');
};
